import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import {
  isPrecachedAppShellPath,
  isStaleClientBuild,
  shouldOfferUpdate,
  updateApplyStrategy,
} from '@pcp/shared';
import { WorkingSpinner } from './WorkingSpinner';
import { ToastLayer } from './ToastContainer';
import { useDeployStatus } from '../lib/useDeployStatus';
import { hasDirtyEdit, onEditsClear } from '../lib/editRegistry';
import { deployedBuildVersion } from '../lib/chunkReload';
import { APP_VERSION } from '../lib/version';

// Reload exactly once, no matter how many of the signals below race in
// (controllerchange, the waiting worker's statechange, the fallback timer).
// But never reload out from under an in-progress edit: while any edit surface is
// dirty (useActiveEditStore), defer the reload and fire it once the edit clears —
// the freshly-activated worker still picks up the new version, just at a safe
// moment instead of flashing the page and dropping the user's focus (b-00171).
let reloading = false;
let reloadDeferred = false;
function reloadOnce() {
  if (reloading) return;
  if (hasDirtyEdit()) {
    if (reloadDeferred) return;
    reloadDeferred = true;
    onEditsClear(() => { reloadDeferred = false; reloadOnce(); });
    return;
  }
  reloading = true;
  window.location.reload();
}

// Always-on safety net: when a new service worker takes control of a page that
// was already controlled (a real update — not the very first install), reload.
// This un-wedges every OTHER open tab/window the moment the update is accepted
// in one of them. Without it those tabs kept running old code behind a stale
// prompt whose button no-opped: their `registration.waiting` was already gone,
// so workbox-window's messageSkipWaiting() silently did nothing.
if ('serviceWorker' in navigator) {
  const wasControlled = !!navigator.serviceWorker.controller;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (wasControlled) reloadOnce();
  });
}

// ─── Persisted across the reload, because that is the whole bug ──────────────
//
// b-01989: both of these used to be in-memory React state, so a reload — the
// one thing the user actually does — wiped them. The dismissal is keyed to the
// version it dismissed so the NEXT release still prompts exactly once, and the
// attempt count is what lets a second click escalate past a handoff that has
// already been shown not to work here.
const DISMISSED_KEY = 'pcp:update-dismissed-version';
const ATTEMPTS_KEY = 'pcp:update-handoff-attempts';

function readStored(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function writeStored(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* private mode — degrade */ }
}
function clearStored(key: string): void {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}

/** Handoff attempts already made for `version` (0 for any other version). */
function readAttempts(version: string | null): number {
  if (!version) return 0;
  try {
    const raw = readStored(ATTEMPTS_KEY);
    if (!raw) return 0;
    const parsed = JSON.parse(raw) as { version?: unknown; count?: unknown };
    return parsed?.version === version ? Number(parsed.count) || 0 : 0;
  } catch {
    return 0;
  }
}
function noteAttempt(version: string | null): void {
  if (!version) return;
  writeStored(ATTEMPTS_KEY, JSON.stringify({ version, count: readAttempts(version) + 1 }));
}

/**
 * Last resort: drop the service worker and every cache, then reload.
 *
 * Needed because `/` is answered from the worker's own precache (`index.html`
 * is in the manifest and workbox maps `/` onto it), so while a wedged worker
 * stays in control a reload just re-serves the old shell — forever. Unregister
 * first so the reload is answered by the network, then clear the caches so the
 * next worker cannot re-serve the old precache either.
 *
 * Never throws: every step is individually best-effort, and the reload happens
 * regardless so the user is not left staring at a dead button.
 */
/**
 * Re-confirm against the NETWORK that we are still behind.
 *
 * Hard recovery is destructive, and `navigator.onLine` lies — it is true behind
 * a captive portal, on connected-but-dead Wi-Fi, and with a required VPN down.
 * The unique query string misses the worker's NetworkFirst `api-cache`, so this
 * can only be answered by a live network: a throw means no connectivity, and
 * binning the caches then would strand the user on the browser's offline page
 * with no precache to come back to.
 *
 * Answers false for anything it cannot prove, which always means "do not wipe".
 */
async function confirmStillStale(): Promise<boolean> {
  try {
    const res = await fetch(`/api/health?fresh=${Date.now()}`, { cache: 'no-store' });
    const body = await res.json().catch(() => null);
    const v = (body as { deployment?: unknown } | null)?.deployment;
    if (typeof v !== 'string' || !v) return false;
    return isStaleClientBuild(APP_VERSION, v);
  } catch {
    return false;
  }
}

async function hardRecover(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
    }
  } catch { /* ignore */ }
  try {
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)));
    }
  } catch { /* ignore */ }
  reloadOnce();
}

// `useRegisterSW` re-registers on every mount, and AuthGate unmounts this whole
// tree on the biometric lock — so a long session used to stack one more 5-minute
// interval and one more visibilitychange listener per unlock. Install them once.
let updateWatchInstalled = false;
function installUpdateWatch(r: ServiceWorkerRegistration): void {
  if (updateWatchInstalled) return;
  updateWatchInstalled = true;
  // The browser only checks for a new service worker on a full page load, so
  // with the SPA kept open the update prompt would never appear on its own.
  // Poll periodically and whenever the tab regains focus so a freshly
  // deployed version surfaces the prompt without a manual hard refresh.
  const checkForUpdate = () => { r.update().catch(() => { /* offline — ignore */ }); };
  setInterval(checkForUpdate, 5 * 60 * 1000); // every 5 minutes
  // Debounce the focus-driven check: right after a reload the tab regains
  // focus immediately, and an unsettled rollout could surface yet another
  // waiting worker (~5s later) — the user's reported "another toast". Coalesce
  // rapid focus events into one check 10s after focus settles.
  let focusTimer: ReturnType<typeof setTimeout> | undefined;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    if (focusTimer) clearTimeout(focusTimer);
    focusTimer = setTimeout(checkForUpdate, 10_000);
  });
}

export function ReloadPrompt() {
  // True from the moment "Reload" is clicked until the page navigates — the
  // handoff to the new worker can take a few seconds (8s worst case via the
  // fallback timer), so the button shows a spinner instead of going dead.
  const [updating, setUpdating] = useState(false);
  // Set when the user clicks Reload while an edit is dirty: the SW handoff is
  // armed but the page reload is deferred until the edit clears (reloadOnce), so
  // we show a "pending" message instead of a spinner that never resolves.
  const [pendingAfterEdit, setPendingAfterEdit] = useState(false);
  // The deployed version the user dismissed with ✕ — read from storage on mount
  // so ✕ actually survives the reload (it did not, which is half of b-01989).
  const [dismissedVersion, setDismissedVersion] = useState<string | null>(() => readStored(DISMISSED_KEY));
  // Suppress the prompt entirely while a deploy is rolling — assets/sw.js are
  // still churning, so prompting now guarantees a burst (the ReleaseBanner
  // already covers this window). React Query holds the last inProgress:true
  // across the API-outage polls, so suppression survives the unstable window;
  // once it clears, a genuinely stale build surfaces exactly one prompt.
  const { inProgress: deployInProgress } = useDeployStatus();

  // Offline, /api/health is answered from the worker's own cache, so the version
  // it reports may be one we have since moved past — which compares as skew.
  // Same `navigator.onLine === false` idiom the chunk/build self-heals use.
  const [online, setOnline] = useState(
    () => !(typeof navigator !== 'undefined' && navigator.onLine === false),
  );
  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // What the server is ACTUALLY serving. This — not "a worker is waiting" — is
  // what decides whether there is an update, so the prompt can finally tell
  // "you are behind" from "you are already up to date" (b-01989). Same reader
  // the error boundary's self-heal uses, so there is one definition of it.
  const { data: deployedVersion } = useQuery({
    queryKey: ['deployed-build-version'],
    queryFn: deployedBuildVersion,
    refetchInterval: 5 * 60 * 1000,
    refetchIntervalInBackground: false,
    // The global client disables focus refetching, but a backgrounded tab pauses
    // the interval — without this the toast could lag a release by 5 minutes
    // while the service-worker check already fires 10s after focus. staleTime
    // keeps that to at most one request a minute.
    refetchOnWindowFocus: true,
    staleTime: 60_000,
  });
  const deployed = deployedVersion ?? null;

  // `needRefresh` is deliberately NOT read: it means "a worker is waiting",
  // which re-arms on every load and is what made this toast permanent. The
  // offline row is the only thing this hook still drives.
  const {
    offlineReady: [offlineReady, setOfflineReady],
  } = useRegisterSW({
    onRegistered(r) {
      if (r) installUpdateWatch(r);
    },
    onRegisterError(error) {
      console.log('SW registration error', error)
    },
  })

  // Running the deployed build → forget the markers, so the next real release is
  // offered cleanly and a stale "already dismissed"/"already tried" can't leak
  // into it. This is also what makes a successful update self-clean.
  useEffect(() => {
    if (!deployed) return;
    if (isStaleClientBuild(APP_VERSION, deployed)) return;
    clearStored(DISMISSED_KEY);
    clearStored(ATTEMPTS_KEY);
    setDismissedVersion((prev) => (prev === null ? prev : null));
  }, [deployed]);

  const close = () => {
    setOfflineReady(false)
    // Sticky, and keyed to the version being dismissed — a later release differs
    // from the stored one, so it prompts again (exactly once). Recorded ONLY when
    // there is really an update to dismiss: this ✕ also closes the independent
    // "Ready to work offline" row, and banking a dismissal for that would
    // silence the next genuine update prompt.
    if (deployed && isStaleClientBuild(APP_VERSION, deployed)) {
      writeStored(DISMISSED_KEY, deployed);
      setDismissedVersion(deployed);
    }
  }

  /**
   * The destructive rung, behind the two guards it must never skip.
   *
   *  • An unsaved edit: hardRecover's reload is deferred until the edit clears
   *    (reloadOnce), so wiping now would leave the tab running with no worker
   *    and no caches for as long as the edit stays open — possibly an hour.
   *    Re-run the whole decision once it clears instead.
   *  • No proven connectivity: binning the caches without a network to refetch
   *    from strands the user on the browser's offline page.
   */
  const escalate = async (): Promise<void> => {
    if (hasDirtyEdit()) {
      setPendingAfterEdit(true);
      onEditsClear(() => { void escalate(); });
      return;
    }
    if (!(await confirmStillStale())) {
      // Either we are current after all, or there is no network to fetch the new
      // build with. Neither is worth wiping the offline shell for.
      setUpdating(false);
      setPendingAfterEdit(false);
      return;
    }
    noteAttempt(deployed);
    await hardRecover();
  };

  const reload = async () => {
    // Drive the handoff against the LIVE registration instead of relying on
    // updateServiceWorker(true) (workbox-window). That path had two silent
    // dead-ends that made this button "do nothing":
    //  1. messageSkipWaiting() no-ops when `registration.waiting` is already
    //     gone (another tab/window accepted the update, or a previous click
    //     activated the worker without reloading).
    //  2. Its reload only fires when the `controlling` event has isUpdate=true,
    //     which workbox-window derives from "was this page controlled at
    //     register time" — false after a hard refresh, so the worker activated
    //     but the page never reloaded and the prompt kept coming back.
    // We still only reload AFTER the new worker has actually taken over (the
    // statechange/controllerchange listeners) — reloading before activation is
    // what used to cause the endless re-prompt loop.
    // While mid-edit the reload is deferred (reloadOnce waits for edits to
    // clear), so reflect that rather than stranding the button on a spinner that
    // never resolves. The SW handoff below still runs, so the new version is
    // active and ready to load the moment the edit is saved/closed.
    if (hasDirtyEdit()) setPendingAfterEdit(true);
    else setUpdating(true);
    try {
      const reg = 'serviceWorker' in navigator
        ? await navigator.serviceWorker.getRegistration()
        : undefined;
      const attempts = readAttempts(deployed);
      // Ask the live registration, not `needRefresh`: it is the authority on
      // whether there is anything to hand off to.
      let waiting = reg?.waiting;
      if (reg && !waiting && attempts === 0) {
        // No worker waiting YET does not mean none is coming. The toast is on
        // screen within a few hundred ms of first paint (it only needs
        // /api/health), while the new worker is not `waiting` until it has
        // finished precaching — seconds to minutes on a phone. Give the browser
        // a bounded chance to produce one rather than binning the whole
        // precache on a first tap that was merely early.
        await Promise.race([
          reg.update().catch(() => undefined),
          new Promise((r) => setTimeout(r, 5000)),
        ]);
        waiting = reg.waiting;
      }
      const strategy = updateApplyStrategy({
        workerWaiting: !!waiting,
        previousAttempts: attempts,
        pathServedFromPrecache: isPrecachedAppShellPath(window.location.pathname),
      });
      // Every branch below records the attempt only once it actually commits to
      // an action — a deferred or abandoned attempt must not burn the budget and
      // push the next click further down the ladder.
      if (strategy !== 'skip-waiting' || !waiting) {
        // Off the app shell (and once wiping has had its chances) a reload is
        // enough: the navigation is not answered by the worker, so it lands on
        // the fresh shell without destroying anything.
        // (`!waiting` cannot be true for 'skip-waiting'; it is repeated so the
        // code below narrows to a live worker.)
        if (strategy === 'plain-reload') {
          noteAttempt(deployed);
          reloadOnce();
          return;
        }
        await escalate();
        return;
      }
      noteAttempt(deployed);
      const activating = waiting;
      activating.addEventListener('statechange', () => {
        // Covers pages the module-level controllerchange listener can't: after
        // a hard refresh this page is uncontrolled, but the worker still
        // reaches "activated".
        if (activating.state === 'activated') reloadOnce();
      });
      activating.postMessage({ type: 'SKIP_WAITING' });
      // Last resort for a wedged handoff. It used to be a blind reload, which on
      // a precached `/` re-served the very build we were trying to leave and
      // re-armed the toast; escalate through the same guards instead.
      setTimeout(() => {
        if (reloading || reloadDeferred) return;
        void escalate();
      }, 8000);
    } catch {
      reloadOnce();
    }
  };

  // Offer the update when the SERVER says it is serving a different build —
  // never merely because a worker sits in `waiting`. workbox re-fires `waiting`
  // on every registration when one is already waiting, and a plain reload never
  // releases the old worker, so the old condition re-armed on every load and
  // could not tell it was already up to date (b-01989).
  const showUpdate = shouldOfferUpdate({
    running: APP_VERSION,
    deployed,
    dismissedForVersion: dismissedVersion,
    deployInProgress,
    online,
  });
  if (!offlineReady && !showUpdate) return null;

  // The two rows are no longer mutually exclusive. `offlineReady` comes from the
  // worker's first install while `showUpdate` comes from a version comparison
  // that knows nothing about worker state, so both can be true — most easily
  // right after a hard recovery installs a fresh worker. An update is the more
  // urgent and more actionable of the two, so it wins the row outright rather
  // than showing "Ready to work offline" next to a Reload button.
  const showOffline = offlineReady && !showUpdate;

  // Rendered as a standard house toast (same markup/classes as ToastContainer)
  // so it tracks the theme: glass `.toast` row, accent-soft secondary action,
  // ghost ✕ dismiss. No bespoke card, no oversized buttons. In the same toast
  // layer too, so an open sheet or popover cannot hide it (b-02055).
  return (
    <ToastLayer>
      <div className={`toast ${showOffline ? 'toast-success' : 'toast-info'}`} role="alert">
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          {showOffline
            ? '✓'
            : <Sparkles size={15} style={{ color: 'var(--accent-primary)' }} aria-hidden />}
        </span>
        <span style={{ flex: 1 }}>
          {showOffline
            ? 'Ready to work offline'
            : pendingAfterEdit
              ? 'Update ready — applies when you finish your edit'
              : 'New version available'}
        </span>
        {showUpdate && !pendingAfterEdit && (
          <button
            className="btn btn-secondary btn-sm"
            style={{ flexShrink: 0 }}
            aria-busy={updating}
            onClick={() => { if (!updating) void reload(); }}
          >
            {updating ? <WorkingSpinner size={12} title="Reloading…" /> : null}
            {updating ? 'Reloading…' : 'Reload'}
          </button>
        )}
        <button
          className="btn-ghost"
          title="Dismiss"
          style={{ padding: 'var(--space-1)', opacity: 0.6, fontSize: 'var(--text-xs)' }}
          onClick={() => { if (!updating) close(); }}
        >
          ✕
        </button>
      </div>
    </ToastLayer>
  )
}
